import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { sanitizeErrorMessage } from '@/lib/error-utils';

const ERROR_CODES = [
  'consent_denied',
  'unverified_email',
  'no_account',
  'account_inactive',
  'state_expired',
  'invalid_state',
  'oauth_failed',
  'rate_limited',
  'registration_failed',
] as const;

export default function GoogleCallbackPage() {
  const [, setLocation] = useLocation();
  const { t } = useTranslation(['auth']);
  const { toast } = useToast();
  const { loginWithGoogleCode } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    // Guard against double-invocation (React StrictMode) — the one-time
    // code is single use on the backend
    if (handled.current) return;
    handled.current = true;
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async () => {
    // The backend hands the code back in the fragment, which browsers never
    // send to a server — so it stays out of access logs and Referer headers.
    // The query string is still read as a fallback, since the API and the SPA
    // deploy separately and an older API may still redirect the old way.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const queryParams = new URLSearchParams(window.location.search);
    const code = hashParams.get('code') ?? queryParams.get('code');
    const errorCode = hashParams.get('error_code') ?? queryParams.get('error_code');

    // Drop it from the address bar before anything else, so it never reaches
    // session history.
    window.history.replaceState(null, '', window.location.pathname);

    if (errorCode || !code) {
      const known = ERROR_CODES.includes(errorCode as (typeof ERROR_CODES)[number]);
      toast({
        title: t('auth:google.errorTitle'),
        description: t(known ? `auth:google.errors.${errorCode}` : 'auth:google.error'),
        variant: 'destructive',
      });
      // Unknown accounts should register instead — /register is the live page.
      setLocation(errorCode === 'no_account' ? '/register' : '/login');
      return;
    }

    try {
      // The context handles the redirect itself: a first-time signup lands on
      // onboarding, a returning user on their dashboard.
      await loginWithGoogleCode(code);
    } catch (error: any) {
      toast({
        title: t('auth:google.errorTitle'),
        description: sanitizeErrorMessage(error, t('auth:google.error')),
        variant: 'destructive',
      });
      setLocation('/login');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen flex items-center justify-center">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <div className="p-4 bg-primary/10 rounded-full">
              <RefreshCw className="h-12 w-12 text-primary animate-spin" />
            </div>
            <div>
              <h2 className="text-h1 text-ink mb-2">{t('auth:google.completing')}</h2>
              <p className="text-soft">{t('auth:google.completingDescription')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
